import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { formatSignedPoints } from '@/lib/format';
import { cn } from '@/lib/utils';
import { PlayerLink } from '@/pages/shared';

/**
 * The subset of a ranking row the podium renders (season ladder, server top
 * 100 by score or by time). `games` / `points` feed the default title-bar
 * action and figure; a time podium passes its own instead.
 */
export interface PodiumRow {
  name: string;
  avatar: string | null;
  /** Null hides the level line (a winner with no player behind them). */
  level: number | null;
  games?: number;
  points?: number;
  /** False renders the name as plain text: there is no profile to link to. */
  linked?: boolean;
}

interface PodiumCardProps {
  row: PodiumRow | undefined;
  place: number;
  /** Title bar of the #1 card, e.g. "#1 - Season leader". */
  leaderTitle: string;
  /** Label under the figure, e.g. "Season score". */
  caption: string;
  /** Optional badge row between avatar and name (achievement icons). */
  badges?: ReactNode;
  /** Replaces the signed-points figure (e.g. a winning time). */
  figure?: ReactNode;
  /** Replaces the "N games" title-bar action. */
  action?: ReactNode;
}

/**
 * One of the three podium cards above a standings table. The leader card is
 * orange, larger and nudged down so it visually anchors the row. Renders an
 * empty placeholder when the place is not filled so the grid keeps its shape.
 */
export function PodiumCard({
  row,
  place,
  leaderTitle,
  caption,
  badges,
  figure,
  action,
}: PodiumCardProps) {
  const { t } = useTranslation('common');

  if (!row) return <div aria-hidden />;
  const isLeader = place === 1;
  const defaultAction = row.games !== undefined ? t('units.games', { count: row.games }) : null;
  const defaultFigure = row.points !== undefined ? formatSignedPoints(row.points) : null;

  return (
    <BevelWindow
      variant={isLeader ? 'orange' : 'default'}
      shadow={isLeader ? 'lg' : 'sm'}
      title={isLeader ? leaderTitle : `#${place}`}
      action={action ?? defaultAction}
      className={cn(isLeader && 'sm:-mb-2')}
    >
      <div
        className={cn('flex flex-col items-center gap-2 text-center', isLeader ? 'py-4' : 'py-2')}
      >
        <PlayerAvatar name={row.name} src={row.avatar} size={isLeader ? 64 : 52} />
        {badges}
        {row.linked === false ? (
          <span className="text-strong text-base font-semibold">{row.name}</span>
        ) : (
          <PlayerLink name={row.name} className="text-strong text-base font-semibold" />
        )}
        {row.level !== null ? (
          <span className="text-dim font-mono text-xs">
            {t('units.level', { level: row.level })}
          </span>
        ) : null}
        <span
          className={cn('font-display', isLeader ? 'text-gold text-2xl' : 'text-green text-lg')}
        >
          {figure ?? defaultFigure}
        </span>
        <span className="text-faint text-[10px] uppercase tracking-wide">{caption}</span>
      </div>
    </BevelWindow>
  );
}
